import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'
import React, { useEffect } from 'react'

import { IconButton } from '../IconButton'

interface PaginationProps extends React.HTMLAttributes<HTMLElement> {
  pageIndex: number;
  perPage: number;
  totalRows: number;
  onPageChange: (page: number) => void;
}

export function Pagination ({ pageIndex: pageIndexProp, perPage: perPageProp, totalRows: totalRowsProp, onPageChange, ...props }: PaginationProps) {
  const perPage = perPageProp < 1 ? 1 : perPageProp
  const totalPages = Math.ceil(totalRowsProp / perPage)
  const pageIndex = Math.max(0, Math.min(pageIndexProp, totalPages - 1))
  const totalRows = Math.max(0, totalRowsProp)

  useEffect(() => {
    if (perPageProp < 1) {
      console.warn('<Pagination />: `perPage` should be greater than 0. Defaulting to `1`.')
    }
  }, [perPageProp])

  useEffect(() => {
    if (pageIndexProp > Math.max(totalPages - 1, 0)) {
      console.warn(`<Pagination />: \`pageIndex\` of ${pageIndexProp} is greater than the total number of pages. Defaulting to ${totalPages - 1}.`)
      onPageChange(totalPages - 1)
    }

    if (pageIndexProp < 0) {
      console.warn('<Pagination />: `pageIndex` should not be negative. Defaulting to 0.')
      onPageChange(0)
    }
  }, [pageIndexProp, totalPages])

  useEffect(() => {
    if (totalRowsProp < 0) {
      console.warn('<Pagination />: `totalRows` should not be negative. Defaulting to 0.')
    }
  }, [totalRowsProp])

  function renderRange () {
    if (totalRows <= 0) {
      return '0 of 0'
    }

    const startRow = perPage * pageIndex + 1
    const endRow = Math.min(perPage * (pageIndex + 1), totalRows)

    if (startRow === endRow) {
      return `${startRow} of ${totalRows}`
    }

    return (
      <>
        {startRow}
        <span className='mx-px'>–</span>
        {endRow} of {totalRows}
      </>
    )
  }

  return (
    <nav
      {...props}
      className='flex items-center gap-1'
      aria-label={props['aria-label'] ?? 'Pagination'}
    >
      <IconButton
        icon={ChevronLeftIcon}
        label='Previous page'
        disabled={pageIndex === 0}
        onClick={() => {
          onPageChange(pageIndex - 1)
        }}
      />
      <div
        className='text-gray-800 body-sm-bold'
        data-testid='pagination-range'
      >
        {renderRange()}
      </div>
      <IconButton
        icon={ChevronRightIcon}
        label='Next page'
        disabled={totalPages === 0 || pageIndex === totalPages - 1}
        onClick={() => {
          onPageChange(pageIndex + 1)
        }}
      />
    </nav>
  )
}
